import { useCallback } from "react";

import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { v4 as uuidv4 } from "uuid";

import { deserializeJsonApi } from "src/api/jsonApiHelpers";
import { CaseHistoryDeserialized } from "src/cases/queries/casesTypes";
import { useCasesClient } from "src/cases/queries/client";
import {
  CaseCommentCreateRequestDataTypeEnum,
  CaseCommentCreateResponse,
  CaseHistoryEventsResponse,
  GetCaseHistoryApiV1CasesCaseIdHistoryGetPageDirectionEnum,
  JsonApiResourceObjectCaseHistoryEventAttributesBaseModel,
} from "src/clients/case-management/api";
import { DashboardPageParams } from "src/router/urls";
import { useCurrentUserId } from "src/store/AuthStore";
import { useParamsDecode } from "src/utils/useParamsDecode";

// Constants
const HISTORY_PAGE_SIZE = 20;

// Query key factory for cache management
export const historyKeys = {
  all: (workspaceId: string) => ["caseHistory", workspaceId] as const,
  list: (workspaceId: string, caseId: string) =>
    [...historyKeys.all(workspaceId), "list", caseId] as const,
};

// Type definitions
type CaseHistoryParams = {
  caseId?: string;
  pageDirection?: GetCaseHistoryApiV1CasesCaseIdHistoryGetPageDirectionEnum;
};

// Helper hook
const useHistoryParams = () => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const apiClient = useCasesClient();

  return { wsId, apiClient };
};

export const useCreateCaseComment = ({ caseId }: { caseId: string }) => {
  const apiClient = useCasesClient();
  const queryClient = useQueryClient();
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const currentUserId = useCurrentUserId();

  return useMutation<
    CaseCommentCreateResponse,
    AxiosError,
    { comment: string },
    {
      previousCaseHistoryEvents:
        | InfiniteData<CaseHistoryEventsResponse>
        | undefined;
    }
  >({
    mutationFn: async ({ comment }) => {
      if (!apiClient) {
        throw new Error("API client not found");
      }

      const response =
        await apiClient.createCaseCommentApiV1CasesCaseIdCommentsPost({
          caseId,
          caseCommentCreateRequest: {
            data: {
              type: CaseCommentCreateRequestDataTypeEnum.CASE_COMMENTS,
              attributes: {
                comment_text: comment,
              },
            },
          },
        });

      return response.data;
    },
    onMutate: async ({ comment }) => {
      const queryKey = historyKeys.list(wsId, caseId);

      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value for rollback
      const previousCaseHistoryEvents =
        queryClient.getQueryData<InfiniteData<CaseHistoryEventsResponse>>(
          queryKey,
        );

      // Generate a unique ID for the optimistic comment to avoid conflicts
      const optimisticCommentId = uuidv4();

      // Create optimistic comment event with placeholder values
      const optimisticComment: JsonApiResourceObjectCaseHistoryEventAttributesBaseModel =
        {
          type: "case_history_events",
          id: optimisticCommentId,
          attributes: {
            event_type: "comment_added",
            timestamp: new Date().toISOString(),
            user_id: currentUserId ?? null,
            event_data: {
              event_type: "comment_added",
              comment_text: comment,
              comment_id: optimisticCommentId,
              user_id: currentUserId ?? "",
            },
          },
        };

      // Optimistically update the cache
      queryClient.setQueryData<InfiniteData<CaseHistoryEventsResponse>>(
        queryKey,
        (old) => {
          if (!old) {
            return old;
          }

          // Add the optimistic comment to the first page (most recent events)
          // Since backward order is used, newest events are at the top (index 0)
          const firstPage = old.pages[0];
          if (!firstPage) {
            return old;
          }

          return {
            ...old,
            pages: [
              {
                ...firstPage,
                data: [optimisticComment, ...firstPage.data],
              },
              ...old.pages.slice(1),
            ],
          };
        },
      );

      // Return context with previous data for rollback
      return { previousCaseHistoryEvents };
    },
    onError: (_error, _variables, context) => {
      // Rollback to the previous state on error
      if (context?.previousCaseHistoryEvents !== undefined) {
        queryClient.setQueryData(
          historyKeys.list(wsId, caseId),
          context.previousCaseHistoryEvents,
        );
      }
    },
  });
};

/**
 * Fetches case history events with infinite scroll pagination
 * @param params - Query parameters including caseId, pageSize, pageDirection, and sort
 * @returns Infinite query for case history events
 */
export const useCaseHistory = (params: CaseHistoryParams) => {
  const { wsId, apiClient } = useHistoryParams();
  const {
    caseId,
    pageDirection = GetCaseHistoryApiV1CasesCaseIdHistoryGetPageDirectionEnum.BACKWARD,
  } = params;

  const selectHistoryEvents = useCallback(
    (data: InfiniteData<CaseHistoryEventsResponse>) => {
      const events = data.pages.flatMap((page) => page.data);
      const included = data.pages.flatMap((page) => page.included ?? []);

      return deserializeJsonApi<CaseHistoryDeserialized["data"]>({
        data: events,
        included,
      });
    },
    [],
  );

  return useInfiniteQuery<
    CaseHistoryEventsResponse,
    AxiosError,
    ReturnType<typeof selectHistoryEvents>,
    readonly unknown[],
    {
      cursor: string | undefined;
      pageDirection: GetCaseHistoryApiV1CasesCaseIdHistoryGetPageDirectionEnum;
    }
  >({
    queryKey: historyKeys.list(wsId, caseId || ""),
    queryFn: async ({ pageParam, signal }) => {
      if (!apiClient || !caseId) {
        throw new Error("API client or case ID is missing");
      }

      const response = await apiClient.getCaseHistoryApiV1CasesCaseIdHistoryGet(
        {
          caseId,
          pageSize: HISTORY_PAGE_SIZE,
          pageCursor: pageParam.cursor ?? null,
          pageDirection: pageParam.pageDirection,
        },
        { signal },
      );

      return response.data;
    },
    enabled: !!apiClient && !!caseId,
    initialPageParam: {
      cursor: undefined,
      pageDirection,
    },
    select: selectHistoryEvents,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (lastPage.links.next) {
        const url = new URL(lastPage.links.next);
        const search = new URLSearchParams(url.search);
        const cursor = search.get("page[cursor]");

        return {
          cursor: cursor ?? undefined,
          pageDirection: lastPageParam.pageDirection,
        };
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage, _allPages, firstPageParam) => {
      // Get the first event to construct a cursor for fetching previous pages
      const firstEvent = firstPage.data[0];
      if (!firstEvent) {
        return undefined;
      }

      // Extract timestamp and event ID to construct cursor in format "timestamp,event_id"
      const timestamp = firstEvent.attributes.timestamp;
      const eventId = firstEvent.id;

      if (!timestamp || !eventId) {
        return undefined;
      }

      // Reverse the direction for fetching previous pages
      const reversedDirection =
        firstPageParam.pageDirection ===
        GetCaseHistoryApiV1CasesCaseIdHistoryGetPageDirectionEnum.BACKWARD
          ? GetCaseHistoryApiV1CasesCaseIdHistoryGetPageDirectionEnum.FORWARD
          : GetCaseHistoryApiV1CasesCaseIdHistoryGetPageDirectionEnum.BACKWARD;

      // Return cursor and reversed direction in the format expected by the API
      return {
        cursor: `${timestamp},${eventId}`,
        pageDirection: reversedDirection,
      };
    },
  });
};
